import {
  put, takeLatest, takeEvery, select,
} from 'redux-saga/effects';

import ScenesService from '../../../services/ScenesService';
import {
  getScenesListRequest,
  getScenesListSuccess,
  getScenesListFail,
  initScenesRequest,
  initScenesSuccess,
  initScenesFail,
  saveScenesRequest,
  saveScenesSuccess,
  saveScenesFail,
  getScenesRequest,
  getScenesSuccess,
  getScenesFail,
  createScenesRequest,
  createScenesSuccess,
  createScenesFail,
  deleteScenesRequest,
  deleteScenesSuccess,
  deleteScenesFail,
  clearSceneList,
  getScenesListRefreshRequest,
  getScenesListRefreshSuccess,
  setRefreshScenesList,
  setCurrentSceneId,
  setSavingScript,
  setSavedScript,
  setEditingScript,
  duplicateScenesRequest, updateScenesListRequest,
} from './actions';
import { logOut } from '../auth/actions';
import { selectListScenes } from './selectors';
import { apiCall, forEachGen } from '../../effects';
import { getErrorNotification, getQueryParamsByItemList } from '../../util';
import { notify } from '../notifications/actions';
import { selectProject } from '../projects/selectors';
import { saveProjectSetting, getProjectSettings } from '../../../services/local-storage/projectStorage';
import { removeQueryParams } from '../../../utility/helpers';
import { getQueryParameter } from '../../../utility/queryParams';
import { Scenes } from '../../../types/items';

function* getScenesList({
  payload,
}: ReturnType<typeof getScenesListRequest>) {
  try {
    const list = yield apiCall(ScenesService.list, payload);
    yield put(getScenesListSuccess(list));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(getScenesListFail());
  }
}

function* getScenes({ payload }: ReturnType<typeof getScenesRequest>) {
  try {
    const response = yield apiCall(ScenesService.get, payload);
    yield put(getScenesSuccess(response));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(getScenesFail());
  }
}

function* saveScenes({ payload }: ReturnType<typeof saveScenesRequest>) {
  try {
    if (!payload.notifyLastScriptAutoSave) {
      yield put(setEditingScript(false));
      yield put(setSavingScript(true));
    }
    const response = yield apiCall(ScenesService.save, payload.id, payload);
    yield put(saveScenesSuccess(response));
    if (!payload.notifyLastScriptAutoSave) {
      yield put(setSavingScript(false));
      yield put(setSavedScript(true));
    } else {
      yield put(notify({
        type: 'success',
        message: 'The last script was auto-saved',
      }));
    }
  } catch (e) {
    yield put(setEditingScript(true));
    yield put(setSavingScript(false));
    yield put(notify(getErrorNotification(e)));
    yield put(saveScenesFail());
  }
}

function* updateSceneListRequest({ payload }: ReturnType<typeof updateScenesListRequest>) {
  function* updateSceneRequest(scene : Scenes) {
    yield apiCall(ScenesService.save, scene.id, scene);
  }
  yield forEachGen(payload, updateSceneRequest);
}

function* getTemplateScenes() {
  try {
    const response = yield apiCall(ScenesService.template);
    yield put(initScenesSuccess(response));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(initScenesFail());
  }
}

function* createScenes({ payload }: ReturnType<typeof createScenesRequest>) {
  try {
    const response = yield apiCall(ScenesService.create, payload);
    yield put(createScenesSuccess(response));
    yield put(setCurrentSceneId(response.id));
  } catch (e) {
    yield put(initScenesSuccess(payload));
    yield put(notify(getErrorNotification(e)));
    yield put(createScenesFail());
  }
}

function* duplicateScenes({ payload }: ReturnType<typeof createScenesRequest>) {
  try {
    const response = yield apiCall(ScenesService.duplicate, payload);
    yield put(createScenesSuccess(response));
    yield put(setCurrentSceneId(response.id));
  } catch (e) {
    yield put(initScenesSuccess(payload));
    yield put(notify(getErrorNotification(e)));
    yield put(createScenesFail());
  }
}

function* deleteScenes({ payload }: ReturnType<typeof deleteScenesRequest>) {
  try {
    yield apiCall(ScenesService.delete, payload);
    yield put(deleteScenesSuccess(payload));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(deleteScenesFail());
  }
}

function* deleteSuccessScenes() {
  const scenes = yield select(selectListScenes);
  const firstSceneId = scenes.data?.items[0]?.id;

  if (firstSceneId) {
    yield put(setCurrentSceneId(firstSceneId));
  } else if (!scenes.data?.items.length) {
    removeQueryParams();
  }
}
function* clearList() {
  yield put(clearSceneList());
}

function* refreshSceneState({ payload }: ReturnType<typeof setRefreshScenesList>) {
  try {
    if (payload) {
      const { data } = yield select(selectListScenes);
      if (data !== null) {
        const params = getQueryParamsByItemList(data);
        yield put(getScenesListRefreshRequest(params));
      }
      yield put(setRefreshScenesList(false));
    }
  } catch (e) {
    console.log(e);
  }
}

function* getScenesListRefresh({ payload }: ReturnType<typeof getScenesListRefreshRequest>) {
  try {
    const list = yield apiCall(ScenesService.list, payload);
    yield put(getScenesListRefreshSuccess(list));
  } catch (e) {
    console.log(e);
  }
}

function* saveToLocalStorageCurrentScriptId({ payload }: ReturnType<typeof setCurrentSceneId>) {
  try {
    const project = yield select(selectProject);
    if (project.data) {
      saveProjectSetting(project.data.id, 'selectedScriptId', payload);
    }
  } catch (e) {
    console.error(e);
  }
}

function* setCurrentScriptIdByProject({ payload }: ReturnType<typeof getScenesListRequest>) {
  try {
    const { data: project } = yield select(selectProject);
    const sceneListPayload = yield select(selectListScenes);
    const sceneIdList = sceneListPayload.data?.items?.map((scene: Scenes) => scene.id) || [];
    const settings = yield getProjectSettings(project?.id);
    const queryScriptId = Number(getQueryParameter('scriptId'));

    /**
     * 1 - set scene from url if exists
     * 2 - set scene from local storage if exists
     * 3 - set first scene
     */

    if (sceneIdList.length) {
      if (sceneIdList.includes(queryScriptId)) {
        yield put(setCurrentSceneId(queryScriptId));
      } else if (settings && settings.selectedScriptId) {
        yield put(setCurrentSceneId(settings.selectedScriptId));
      } else {
        yield put(setCurrentSceneId(sceneIdList[0]));
      }
    }
  } catch (e) {
    console.error(e);
  }
}

export default [
  takeLatest(getScenesListRequest.type, getScenesList),
  takeLatest(getScenesRequest.type, getScenes),
  takeEvery(saveScenesRequest.type, saveScenes),
  takeLatest(initScenesRequest.type, getTemplateScenes),
  takeLatest(createScenesRequest.type, createScenes),
  takeLatest(deleteScenesRequest.type, deleteScenes),
  takeLatest(deleteScenesSuccess.type, deleteSuccessScenes),
  takeLatest(logOut.type, clearList),
  takeLatest(setRefreshScenesList.type, refreshSceneState),
  takeLatest(getScenesListRefreshRequest.type, getScenesListRefresh),
  takeLatest(setCurrentSceneId.type, saveToLocalStorageCurrentScriptId),
  takeLatest(getScenesListSuccess.type, setCurrentScriptIdByProject),
  takeLatest(duplicateScenesRequest.type, duplicateScenes),
  takeLatest(updateScenesListRequest.type, updateSceneListRequest),
];
